import React from "react";
import { Divider, Row, Typography } from "antd";

const { Text } = Typography;

type SectionTitleProps = {
  title: React.ReactNode;
  actions?: React.ReactNode;
  marginTop?: string | number;
  /** @deprecated Use marginTop instead */
  style?: React.CSSProperties;
  className?: string;
};

/**
 * SectionTitle / SectionHeader component
 * 
 * A standardized section header with title on the left, optional actions on the right,
 * and a full-width underline spanning the entire row.
 * 
 * @param title - Section title text or custom ReactNode
 * @param actions - Optional action buttons/icons (right-aligned)
 * @param marginTop - Top margin (default: "48px", use 0 or small value for first section)
 * @param className - Additional CSS classes
 * 
 * @example
 * ```tsx
 * <SectionTitle 
 *   title="Users" 
 *   actions={
 *     <Button icon={<PlusOutlined />} aria-label="Add user">Add</Button>
 *   }
 * />
 * ```
 */
const SectionTitle = ({ title, actions, marginTop = "48px", style, className }: SectionTitleProps) => {
  return (
    <div style={{ marginTop, ...style }} className={className}>
      <Row
        justify="space-between"
        align="middle"
        style={{ gap: 12, flexWrap: "wrap" }}
      >
        {typeof title === "string" ? (
          <Text strong style={{ fontSize: 16 }}>
            {title}
          </Text>
        ) : (
          title
        )}
        {actions && (
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {actions}
          </div>
        )}
      </Row>
      <Divider style={{ marginTop: "8px", marginBottom: "16px" }} />
    </div>
  );
};

// Export as both names for backward compatibility
export default SectionTitle;
export { SectionTitle as SectionHeader };
